import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import {
  fetchAsinWatchlist,
  addSingleAsin,
  deleteSingleAsin,
  deleteAsinsBatch,
  exportAsins,
  fetchPriceAlertInfo,
  setFilter,
  resetFilters,
  toggleSelected,
  setSelected,
  clearSelected,
  removeAsinsLocally,
} from "../redux/slices/asinWatchlistSlice";
import {
  fetchBlacklist,
  addToBlacklist,
  removeFromBlacklist,
  setBlacklistFilter,
  resetBlacklistFilters,
  toggleBlacklistSelected,
  setBlacklistSelected,
  clearBlacklistSelected,
  removeBlacklistAsinsLocally,
} from "../redux/slices/blacklistSlice";
import Pagination from "../components/Pagination";
import DeletingConfirmation from "./DeletingConfirmation";
import CommonModal from "../components/CommonModal";

const PAGE_SIZE_OPTIONS = [100, 250, 500, 1000];

const formatNumber = (num) =>
  typeof num === "number" ? num.toLocaleString("de-DE") : num ?? "–";

const includesCi = (haystack, needle) => {
  if (!needle) return true;
  if (haystack == null) return false;
  return String(haystack).toLowerCase().includes(needle.toLowerCase());
};

const AsinManagementModal = ({
  onClose,
  onShowAsinsModal,
  onSwitchToBlacklist,
  onSwitchToWatchlist,
  mode = "watchlist",
}) => {
  const dispatch = useDispatch();
  const isBlacklist = mode === "blacklist";

  const watchlistState = useSelector((s) => s.asinWatchlist);
  const blacklistState = useSelector((s) => s.blacklist);

  const items = isBlacklist ? blacklistState.items : watchlistState.watchlist;
  const filters = isBlacklist ? blacklistState.filters : watchlistState.filters;
  const selected = isBlacklist ? blacklistState.selected : watchlistState.selected;
  const status = isBlacklist ? blacklistState.status : watchlistState.status;

  const trackedAsins = watchlistState.trackedAsins;
  const asinTokens = watchlistState.asinTokens;
  const asinsLeft = watchlistState.asinsLeft;
  const asinsLocked = watchlistState.asinsLocked;
  const blacklistTotal = blacklistState.totalItems;
  const blacklistMax = blacklistState.maxBlacklist;

  const actions = useMemo(
    () =>
      isBlacklist
        ? {
            fetchList: fetchBlacklist,
            removeBatch: removeFromBlacklist,
            removeSingle: (asin) => removeFromBlacklist([asin]),
            addSingle: (asin) => addToBlacklist([asin]),
            setFilter: setBlacklistFilter,
            resetFilters: resetBlacklistFilters,
            toggleSelected: toggleBlacklistSelected,
            setSelected: setBlacklistSelected,
            clearSelected: clearBlacklistSelected,
            removeLocally: removeBlacklistAsinsLocally,
          }
        : {
            fetchList: fetchAsinWatchlist,
            removeBatch: deleteAsinsBatch,
            removeSingle: deleteSingleAsin,
            addSingle: addSingleAsin,
            setFilter: setFilter,
            resetFilters: resetFilters,
            toggleSelected: toggleSelected,
            setSelected: setSelected,
            clearSelected: clearSelected,
            removeLocally: removeAsinsLocally,
          },
    [isBlacklist]
  );

  const labels = isBlacklist
    ? {
        headerTitle: "Blacklist",
        headerSubtitle: "verwalten",
        emptyTitle: "Blacklist ist leer",
        emptyHint: "ASINs landen hier, wenn du sie beim Löschen blacklist-markierst.",
        addPlaceholder: "ASIN blacklisten",
        addAria: "Zur Blacklist hinzufügen",
      }
    : {
        headerTitle: "ASINs",
        headerSubtitle: "verwalten",
        emptyTitle: "Keine ASINs gefunden",
        emptyHint: "Passe deine Filter an oder füge neue ASINs hinzu.",
        addPlaceholder: "ASIN hinzufügen",
        addAria: "ASIN hinzufügen",
      };

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(100);
  const [asinInput, setAsinInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [confirmBulkDelete, setConfirmBulkDelete] = useState(false);
  const [alsoBlacklistOnDelete, setAlsoBlacklistOnDelete] = useState(false);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(() =>
    Boolean(
      filters.title ||
        filters.brand ||
        filters.category ||
        filters.bsrMin ||
        filters.bsrMax
    )
  );

  const isLoading = status === "loading" && items.length === 0;

  const tableContainerRef = useRef(null);
  const [skeletonRowCount, setSkeletonRowCount] = useState(20);
  const SKELETON_ROW_HEIGHT = isBlacklist ? 33 : 42;

  useLayoutEffect(() => {
    if (!isLoading) return;
    const container = tableContainerRef.current;
    if (!container) return;
    const update = () => {
      setSkeletonRowCount(Math.max(5, Math.ceil(container.clientHeight / SKELETON_ROW_HEIGHT)));
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(container);
    return () => ro.disconnect();
  }, [isLoading]);

  const fetchAll = () => {
    dispatch(actions.fetchList());
  };

  useEffect(() => {
    fetchAll();
    if (!isBlacklist) {
      dispatch(fetchPriceAlertInfo());
    }
    return () => {
      dispatch(actions.clearSelected());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, isBlacklist]);

  useEffect(() => {
    setPage(0);
  }, [filters, pageSize]);

  const filteredItems = useMemo(() => {
    const search = (filters.search || "").trim();

    if (isBlacklist) {
      if (!search) return items;
      return items.filter((item) => includesCi(item.asin, search));
    }

    const bsrMin = filters.bsrMin === "" ? null : Number(filters.bsrMin);
    const bsrMax = filters.bsrMax === "" ? null : Number(filters.bsrMax);

    return items.filter((item) => {
      if (search) {
        const hit =
          includesCi(item.asin, search) ||
          includesCi(item.title, search) ||
          includesCi(item.brand, search) ||
          includesCi(item.category, search);
        if (!hit) return false;
      }
      if (!includesCi(item.title, filters.title)) return false;
      if (!includesCi(item.brand, filters.brand)) return false;
      if (!includesCi(item.category, filters.category)) return false;
      if (bsrMin !== null && !Number.isNaN(bsrMin)) {
        if (item.bsr == null || item.bsr < bsrMin) return false;
      }
      if (bsrMax !== null && !Number.isNaN(bsrMax)) {
        if (item.bsr == null || item.bsr > bsrMax) return false;
      }
      return true;
    });
  }, [items, filters, isBlacklist]);

  const totalPages = Math.max(1, Math.ceil(filteredItems.length / pageSize));
  const pageItems = useMemo(
    () => filteredItems.slice(page * pageSize, (page + 1) * pageSize),
    [filteredItems, page, pageSize]
  );

  const visibleAsins = pageItems.map((it) => it.asin);
  const allVisibleSelected =
    visibleAsins.length > 0 && visibleAsins.every((a) => selected.includes(a));

  const handleSelectAllVisible = () => {
    if (allVisibleSelected) {
      dispatch(actions.setSelected(selected.filter((a) => !visibleAsins.includes(a))));
    } else {
      dispatch(actions.setSelected(Array.from(new Set([...selected, ...visibleAsins]))));
    }
  };

  const handleSelectAllFiltered = () => {
    dispatch(actions.setSelected(filteredItems.map((it) => it.asin)));
  };

  const handleFilterChange = (key, value) => {
    dispatch(actions.setFilter({ key, value }));
  };

  const handleResetFilters = () => {
    dispatch(actions.resetFilters());
  };

  const handleAddAsin = async () => {
    if (!asinInput.trim()) return;
    setBusy(true);
    try {
      const result = await dispatch(actions.addSingle(asinInput.trim())).unwrap();
      if (result.status === "authorized") {
        const blocked = result.asins_blocked || 0;
        const added = result.asins_added ?? (isBlacklist ? null : 0);

        if (!isBlacklist && blocked > 0 && added === 0) {
          toast.warning("Diese ASIN ist auf deiner Blacklist – erst aus der Blacklist entfernen.");
        } else {
          setAsinInput("");
          toast.success(isBlacklist ? "ASIN zur Blacklist" : "ASIN hinzugefügt");
          fetchAll();
        }
      } else {
        toast.error(result.message || "Konnte ASIN nicht hinzufügen");
      }
    } catch (error) {
      toast.error(error.message || "Konnte ASIN nicht hinzufügen");
    }
    setBusy(false);
  };

  const handleDeleteSingle = async (asin) => {
    dispatch(actions.removeLocally([asin]));
    try {
      const result = await dispatch(actions.removeSingle(asin)).unwrap();
      if (result.status === "authorized") {
        toast.success(isBlacklist ? "Aus Blacklist entfernt" : "ASIN gelöscht");
      } else {
        toast.error(result.message || "Konnte ASIN nicht löschen");
        fetchAll();
      }
    } catch (error) {
      toast.error(error.message || "Konnte ASIN nicht löschen");
      fetchAll();
    }
  };

  const handleBulkDelete = async () => {
    if (selected.length === 0) return;
    setConfirmBulkDelete(false);
    const toDelete = [...selected];
    const shouldBlacklist = !isBlacklist && alsoBlacklistOnDelete;
    setAlsoBlacklistOnDelete(false);
    dispatch(actions.removeLocally(toDelete));
    try {
      const result = await dispatch(actions.removeBatch(toDelete)).unwrap();
      if (result.status === "authorized") {
        toast.success(
          isBlacklist
            ? `${toDelete.length} aus Blacklist entfernt`
            : `${toDelete.length} ASIN(s) gelöscht`
        );
        if (shouldBlacklist) {
          try {
            const bResult = await dispatch(addToBlacklist(toDelete)).unwrap();
            if (bResult.status === "authorized") {
              toast.success(`${bResult.asins_added} auf Blacklist gesetzt`);
            }
          } catch {
            toast.error("Blacklisten fehlgeschlagen");
          }
        }
      } else {
        toast.error(result.message || "Bulk-Delete fehlgeschlagen");
        fetchAll();
      }
    } catch (error) {
      toast.error(error.message || "Bulk-Delete fehlgeschlagen");
      fetchAll();
    }
  };

  const handleExport = async () => {
    if (isBlacklist) {
      const csv = ["ASIN", ...items.map((it) => it.asin)].join("\n");
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "blacklist-export.csv";
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      return;
    }
    try {
      await dispatch(exportAsins()).unwrap();
      toast.success("Export gestartet");
    } catch {
      toast.error("Export fehlgeschlagen");
    }
  };

  const advancedFiltersActive =
    filters.title ||
    filters.brand ||
    filters.category ||
    filters.bsrMin ||
    filters.bsrMax;

  const filtersActive = filters.search || advancedFiltersActive;

  return (
    <div className="bg-bgColor relative w-full rounded-lg shadow-lg flex flex-col h-[90vh]">
      <button
        onClick={onClose}
        className="text-primary leading-none bg-[linear-gradient(180deg,_rgba(38,_247,_184,_0.1)_0%,_rgba(12,_216,_155,_0.1)_100%)] dark:bg-[#27FFBA33] hover:opacity-80 absolute top-2 right-2 rounded p-1 z-10"
        aria-label="Modal schließen">
        <i className="bx bx-x text-2xl leading-none"></i>
      </button>

      <div className="px-5 pt-5 pb-3 pr-12 flex-shrink-0">
        <div className="flex flex-col lg:flex-row gap-3 justify-between items-start lg:items-center mb-3">
          <div className="flex flex-col gap-2">
            <div className="inline-flex items-center bg-background border border-borderBottom rounded p-0.5 self-start">
              <button
                onClick={isBlacklist ? onSwitchToWatchlist : undefined}
                disabled={!isBlacklist || !onSwitchToWatchlist}
                className={`px-3 py-1 rounded text-sm font-semibold transition-colors ${
                  !isBlacklist
                    ? "bg-primary/10 text-primary"
                    : "text-textPrimary hover:text-textTitle disabled:cursor-not-allowed"
                }`}>
                Preiswecker
              </button>
              <button
                onClick={!isBlacklist ? onSwitchToBlacklist : undefined}
                disabled={isBlacklist || !onSwitchToBlacklist}
                className={`px-3 py-1 rounded text-sm font-semibold transition-colors ${
                  isBlacklist
                    ? "bg-primary/10 text-primary"
                    : "text-textPrimary hover:text-textTitle disabled:cursor-not-allowed"
                }`}>
                Blacklist
              </button>
            </div>
            <div className="flex flex-wrap gap-3 items-center">
              {isLoading ? (
                <div className="flex gap-1.5 items-center">
                  <div className="w-10 h-5 bg-gray-200 dark:bg-gray-700 rounded-full animate-pulse" />
                  <div className="w-16 h-5 bg-gray-200 dark:bg-gray-700 rounded-full animate-pulse" />
                  <div className="w-14 h-5 bg-gray-200 dark:bg-gray-700 rounded-full animate-pulse" />
                </div>
              ) : isBlacklist ? (
                <p className="text-textPrimary text-sm">
                  <span className="text-textTitle font-medium">{formatNumber(blacklistTotal)}</span>
                  <span className="opacity-40"> · max {formatNumber(blacklistMax)}</span>
                </p>
              ) : (
                <p className="text-textPrimary text-sm">
                  <span className="text-textTitle font-medium">{formatNumber(trackedAsins)}</span>
                  <span className="opacity-60"> belegt · </span>
                  <span className="text-emerald-500 font-medium">{formatNumber(asinsLeft)}</span>
                  <span className="opacity-60"> frei</span>
                  <span className="opacity-40"> · von {formatNumber(asinTokens)}</span>
                </p>
              )}
              {!isBlacklist && asinsLocked > 0 && (
                <span className="bg-[linear-gradient(216.87deg,_rgba(96,_116,_151,_0.18)_-34.6%,_rgba(161,_195,_253,_0.18)_48.31%,_rgba(96,_116,_151,_0.18)_121.93%)] dark:bg-blue-900 text-[10px] text-blue-600 dark:text-blue-300 px-2 py-[2px] rounded">
                  {formatNumber(asinsLocked)} locked
                </span>
              )}
            </div>
          </div>

          <div className="flex flex-wrap gap-2 items-center">
            <input
              type="text"
              value={asinInput}
              onChange={(e) => setAsinInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleAddAsin()}
              placeholder={labels.addPlaceholder}
              className="text-sm h-8 px-3 bg-background text-textTitle rounded border border-borderBottom focus:outline-none focus:border-primary w-40 uppercase placeholder:normal-case"
            />
            <button
              onClick={handleAddAsin}
              disabled={busy || !asinInput.trim()}
              aria-label={labels.addAria}
              className="bg-[linear-gradient(180deg,_#9DBAF8_0%,_#366CF6_100%)] dark:bg-[linear-gradient(180deg,_rgba(38,_247,_184,_0.1)_0%,_rgba(12,_216,_155,_0.1)_100%)] disabled:opacity-50 hover:opacity-90 dark:hover:bg-[#27FFBA33] text-white h-8 px-3 rounded-md flex items-center gap-1 text-xs font-semibold dark:text-[#27FFBA]">
              <i className="bx bx-plus text-base"></i>
              <span className="hidden sm:inline">Hinzufügen</span>
            </button>
            {!isBlacklist && (
              <button
                onClick={() => {
                  onClose();
                  onShowAsinsModal && onShowAsinsModal();
                }}
                className="bg-[#a1c3fd49] dark:bg-[#A1C3FD2E] hover:bg-[#a1c3fd9a] dark:hover:bg-[#a1c3fd60] text-[#366CF6] dark:text-[#A1C3FD] h-8 px-3 rounded-md flex items-center gap-1 text-xs font-semibold">
                <i className="bx bx-upload text-base"></i>
                <span className="hidden sm:inline">Import</span>
              </button>
            )}
            <button
              onClick={handleExport}
              className="dark:bg-[#9D2CFF38] bg-[#a1c3fd49] hover:bg-[#a1c3fd9a] dark:hover:bg-[#4d1f76bc] text-[#366CF6] dark:text-[#8A59B4] h-8 px-3 rounded-md flex items-center gap-1 text-xs font-semibold">
              <i className="bx bx-download text-base"></i>
              <span className="hidden sm:inline">Export</span>
            </button>
          </div>
        </div>

        <div className="bg-background border border-borderBottom rounded-lg p-2.5 mb-2">
          <div className="flex gap-2 items-stretch">
            <div className="relative flex-1">
              <i className="bx bx-search absolute text-base left-3 top-1/2 transform -translate-y-1/2 text-textPrimary"></i>
              <input
                type="text"
                value={filters.search}
                onChange={(e) => handleFilterChange("search", e.target.value)}
                placeholder={isBlacklist ? "ASIN suchen" : "Produkte suchen"}
                className="w-full h-8 pl-9 pr-3 bg-bgColor text-textTitle text-sm rounded border border-borderBottom focus:outline-none focus:border-primary"
              />
            </div>
            {!isBlacklist && (
              <button
                onClick={() => setShowAdvancedFilters((v) => !v)}
                className="relative h-8 px-3 bg-bgColor text-textTitle text-xs rounded border border-borderBottom hover:border-primary flex items-center gap-1.5 whitespace-nowrap">
                <i className="bx bx-filter-alt text-base"></i>
                Erweiterte Filter
                <i className={`bx ${showAdvancedFilters ? "bx-chevron-up" : "bx-chevron-down"} text-base`}></i>
                {advancedFiltersActive && (
                  <span className="absolute top-1 right-1 w-1.5 h-1.5 rounded-full bg-primary"></span>
                )}
              </button>
            )}
          </div>

          {!isBlacklist && showAdvancedFilters && (
            <>
              <div className="grid grid-cols-2 lg:grid-cols-5 gap-2 mt-2">
                <input
                  type="text"
                  value={filters.title}
                  onChange={(e) => handleFilterChange("title", e.target.value)}
                  placeholder="Titel"
                  className="h-8 px-3 bg-bgColor text-textTitle text-xs rounded border border-borderBottom focus:outline-none focus:border-primary"
                />
                <input
                  type="text"
                  value={filters.brand}
                  onChange={(e) => handleFilterChange("brand", e.target.value)}
                  placeholder="Marke"
                  className="h-8 px-3 bg-bgColor text-textTitle text-xs rounded border border-borderBottom focus:outline-none focus:border-primary"
                />
                <input
                  type="text"
                  value={filters.category}
                  onChange={(e) => handleFilterChange("category", e.target.value)}
                  placeholder="Kategorie"
                  className="h-8 px-3 bg-bgColor text-textTitle text-xs rounded border border-borderBottom focus:outline-none focus:border-primary"
                />
                <input
                  type="number"
                  value={filters.bsrMin}
                  onChange={(e) => handleFilterChange("bsrMin", e.target.value)}
                  placeholder="BSR Min"
                  className="h-8 px-3 bg-bgColor text-textTitle text-xs rounded border border-borderBottom focus:outline-none focus:border-primary"
                />
                <input
                  type="number"
                  value={filters.bsrMax}
                  onChange={(e) => handleFilterChange("bsrMax", e.target.value)}
                  placeholder="BSR Max"
                  className="h-8 px-3 bg-bgColor text-textTitle text-xs rounded border border-borderBottom focus:outline-none focus:border-primary"
                />
              </div>

              <div className="flex items-center mt-2 text-xs text-textPrimary">
                <button
                  onClick={handleResetFilters}
                  className="text-primary hover:opacity-80 disabled:opacity-30 disabled:cursor-not-allowed flex items-center gap-1"
                  disabled={!filtersActive}>
                  <i className="bx bx-reset"></i> Filter zurücksetzen
                </button>
              </div>
            </>
          )}
        </div>

        {selected.length > 0 && (
          <div className="flex justify-between items-center bg-[#FF595933] border border-red-500/30 rounded-lg px-3 py-1.5 mb-2">
            <span className="text-xs text-textTitle">
              <strong>{selected.length}</strong> ausgewählt
              {selected.length < filteredItems.length && filteredItems.length > 0 && (
                <button
                  onClick={handleSelectAllFiltered}
                  className="ml-3 text-primary hover:opacity-80 underline">
                  Alle {formatNumber(filteredItems.length)} Treffer auswählen
                </button>
              )}
            </span>
            <div className="flex gap-2">
              <button
                onClick={() => dispatch(actions.clearSelected())}
                className="text-xs text-textPrimary hover:text-textTitle px-2 py-1">
                Aufheben
              </button>
              <button
                onClick={() => setConfirmBulkDelete(true)}
                disabled={busy}
                className="bg-red-500 hover:bg-red-600 disabled:opacity-50 text-white text-xs font-semibold px-3 py-1 rounded flex items-center gap-1">
                <i className="bx bx-trash"></i>
                {selected.length} {isBlacklist ? "entfernen" : "löschen"}
              </button>
            </div>
          </div>
        )}
      </div>

      <div ref={tableContainerRef} className={`flex-1 min-h-0 pr-5 ${isLoading ? 'overflow-hidden' : 'overflow-auto'}`}>
        <table className="w-full text-xs table-fixed">
          <colgroup>
            <col style={{ width: "44px" }} />
            {!isBlacklist && <col style={{ width: "44px" }} />}
            <col style={{ width: "120px" }} />
            {!isBlacklist && <col />}
            {!isBlacklist && <col style={{ width: "140px" }} />}
            {!isBlacklist && <col style={{ width: "180px" }} />}
            {!isBlacklist && <col style={{ width: "90px" }} />}
            <col style={{ width: "120px" }} />
            <col style={{ width: "70px" }} />
          </colgroup>
          <thead className="sticky top-0 bg-bgColor z-10">
            <tr className="border-b border-borderBottom text-textPrimary uppercase tracking-wide text-[10px]">
              <th className="pl-4 pr-2 py-2 text-left">
                <input
                  type="checkbox"
                  checked={allVisibleSelected}
                  onChange={handleSelectAllVisible}
                  className="w-4 h-4 cursor-pointer accent-primary align-middle"
                  aria-label="Sichtbare auswählen"
                />
              </th>
              {!isBlacklist && <th className="p-2"></th>}
              <th className="p-2 text-left font-medium">ASIN</th>
              {!isBlacklist && <th className="p-2 text-left font-medium">Titel</th>}
              {!isBlacklist && <th className="p-2 text-left font-medium">Marke</th>}
              {!isBlacklist && <th className="p-2 text-left font-medium">Kategorie</th>}
              {!isBlacklist && <th className="p-2 text-right font-medium">BSR</th>}
              <th className="p-2 text-left font-medium">
                {isBlacklist ? "Hinzugefügt" : "Löschbar"}
              </th>
              <th className="p-2 text-right font-medium">Aktionen</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              Array.from({ length: skeletonRowCount }).map((_, i) => (
                <tr key={`sk-${i}`} className="border-b border-borderBottom/40">
                  <td className="pl-4 pr-2 py-2">
                    <div className="w-4 h-4 bg-gray-200 dark:bg-gray-700 rounded-sm animate-pulse" />
                  </td>
                  {!isBlacklist && (
                    <td className="p-1">
                      <div className="w-8 h-8 bg-gray-200 dark:bg-gray-600 rounded-md animate-pulse" />
                    </td>
                  )}
                  <td className="p-2">
                    <div className="w-20 h-4 bg-gray-200 dark:bg-gray-700 rounded-full animate-pulse" />
                  </td>
                  {!isBlacklist && (
                    <td className="p-2">
                      <div className="w-full h-4 bg-gray-200 dark:bg-gray-700 rounded-full animate-pulse" />
                    </td>
                  )}
                  {!isBlacklist && (
                    <td className="p-2">
                      <div className="w-[70px] h-4 bg-gray-200 dark:bg-gray-700 rounded-full animate-pulse" />
                    </td>
                  )}
                  {!isBlacklist && (
                    <td className="p-2">
                      <div className="w-24 h-4 bg-gray-200 dark:bg-gray-700 rounded-full animate-pulse" />
                    </td>
                  )}
                  {!isBlacklist && (
                    <td className="p-2">
                      <div className="w-12 h-4 bg-gray-200 dark:bg-gray-700 rounded-full animate-pulse" />
                    </td>
                  )}
                  <td className="p-2">
                    <div className="w-14 h-4 bg-gray-200 dark:bg-gray-700 rounded-full animate-pulse" />
                  </td>
                  <td className="p-2">
                    <div className="flex justify-end">
                      <div className="w-10 h-4 bg-gray-200 dark:bg-gray-700 rounded-full animate-pulse" />
                    </div>
                  </td>
                </tr>
              ))
            ) : pageItems.length === 0 ? (
              <tr>
                <td colSpan={isBlacklist ? 4 : 9} className="text-center py-12">
                  <p className="text-textTitle text-sm">{labels.emptyTitle}</p>
                  <p className="text-textPrimary text-xs mt-1">{labels.emptyHint}</p>
                </td>
              </tr>
            ) : (
              pageItems.map((item) => {
                const isSelected = selected.includes(item.asin);
                return (
                  <tr
                    key={item.asin}
                    className={`border-b border-borderBottom/40 hover:bg-background/60 transition-colors ${
                      isSelected ? "bg-primary/5" : ""
                    }`}>
                    <td className="pl-4 pr-2 py-2 text-left">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => dispatch(actions.toggleSelected(item.asin))}
                        className="w-4 h-4 cursor-pointer accent-primary align-middle"
                        aria-label={`${item.asin} auswählen`}
                      />
                    </td>
                    {!isBlacklist && (
                      <td className="p-1">
                        {item.image ? (
                          <img
                            src={item.image}
                            alt=""
                            loading="lazy"
                            className="w-8 h-8 object-contain bg-white rounded"
                          />
                        ) : (
                          <div className="w-8 h-8 bg-borderBottom/30 rounded" />
                        )}
                      </td>
                    )}
                    <td className="p-2 text-textTitle font-mono">{item.asin}</td>
                    {!isBlacklist && (
                      <td className="p-2 text-textTitle truncate" title={item.title || ""}>
                        {item.title || "–"}
                      </td>
                    )}
                    {!isBlacklist && (
                      <td className="p-2 text-textPrimary truncate" title={item.brand || ""}>
                        {item.brand || "–"}
                      </td>
                    )}
                    {!isBlacklist && (
                      <td className="p-2 text-textPrimary truncate" title={item.category || ""}>
                        {item.category || "–"}
                      </td>
                    )}
                    {!isBlacklist && (
                      <td className="p-2 text-textTitle text-right tabular-nums">
                        {formatNumber(item.bsr)}
                      </td>
                    )}
                    {isBlacklist ? (
                      <td className="p-2 text-textPrimary tabular-nums">
                        {item.tracked_since || "–"}
                      </td>
                    ) : (
                      <td className="p-2 tabular-nums">
                        {item.deletable_date === "jetzt" ? (
                          <span className="text-emerald-500">jetzt</span>
                        ) : (
                          <span className="text-textPrimary">{item.deletable_date || "–"}</span>
                        )}
                      </td>
                    )}
                    <td className="p-2 text-right">
                      <div className="flex justify-end gap-0.5">
                        <button
                          onClick={() =>
                            window.open(
                              `https://amazon.de/dp/${item.asin}`,
                              "_blank"
                            )
                          }
                          className="text-textPrimary hover:text-textTitle p-1"
                          aria-label="Bei Amazon öffnen">
                          <i className="bx bx-link-external text-sm"></i>
                        </button>
                        <button
                          onClick={() => handleDeleteSingle(item.asin)}
                          disabled={busy}
                          className="text-red-500 hover:text-red-400 p-1 disabled:opacity-30"
                          aria-label={`${item.asin} löschen`}>
                          <i className="bx bx-trash text-sm"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between gap-3 px-5 py-2.5 border-t border-borderBottom flex-shrink-0">
        <div className="text-xs text-textPrimary tabular-nums">
          {isLoading
            ? null
            : filteredItems.length === 0
            ? "Keine Treffer"
            : `${formatNumber(page * pageSize + 1)}–${formatNumber(
                Math.min((page + 1) * pageSize, filteredItems.length)
              )} von ${formatNumber(filteredItems.length)}`}
        </div>

        {totalPages > 1 ? (
          <div className="flex items-center gap-1">
            <button
              onClick={() => setPage(0)}
              disabled={page === 0}
              className="text-textPrimary hover:text-textTitle disabled:opacity-20 disabled:cursor-not-allowed p-1"
              aria-label="Erste Seite">
              <i className="bx bx-chevrons-left text-base"></i>
            </button>
            <button
              onClick={() => setPage(Math.max(0, page - 1))}
              disabled={page === 0}
              className="text-textPrimary hover:text-textTitle disabled:opacity-20 disabled:cursor-not-allowed p-1"
              aria-label="Vorherige Seite">
              <i className="bx bx-chevron-left text-base"></i>
            </button>
            <Pagination
              pageIndex={page}
              totalPages={totalPages}
              setPage={setPage}
            />
            <button
              onClick={() => setPage(Math.min(totalPages - 1, page + 1))}
              disabled={page >= totalPages - 1}
              className="text-textPrimary hover:text-textTitle disabled:opacity-20 disabled:cursor-not-allowed p-1"
              aria-label="Nächste Seite">
              <i className="bx bx-chevron-right text-base"></i>
            </button>
            <button
              onClick={() => setPage(totalPages - 1)}
              disabled={page >= totalPages - 1}
              className="text-textPrimary hover:text-textTitle disabled:opacity-20 disabled:cursor-not-allowed p-1"
              aria-label="Letzte Seite">
              <i className="bx bx-chevrons-right text-base"></i>
            </button>
          </div>
        ) : (
          <div></div>
        )}

        <div className="flex items-center gap-2 text-xs text-textPrimary">
          <label htmlFor="page-size" className="opacity-70">
            Pro Seite
          </label>
          <select
            id="page-size"
            value={pageSize}
            onChange={(e) => setPageSize(Number(e.target.value))}
            className="bg-background border border-borderBottom text-textTitle text-xs rounded px-2 py-1 focus:outline-none focus:border-primary tabular-nums">
            {PAGE_SIZE_OPTIONS.map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
        </div>
      </div>

      <CommonModal
        isOpen={confirmBulkDelete}
        setIsOpen={setConfirmBulkDelete}
        closeModal={() => setConfirmBulkDelete(false)}>
        <DeletingConfirmation
          handleDeleteModal={() => setConfirmBulkDelete(false)}
          handleDelete={handleBulkDelete}
          count={selected.length}
          description={
            isBlacklist
              ? "Diese ASINs werden von der Blacklist entfernt."
              : "Diese ASINs werden aus deiner Watchlist entfernt. Einträge jünger als 2 Tage werden gesperrt und automatisch später entfernt."
          }
          showBlacklistOption={!isBlacklist}
          blacklistChecked={alsoBlacklistOnDelete}
          onBlacklistChange={setAlsoBlacklistOnDelete}
        />
      </CommonModal>
    </div>
  );
};

export default AsinManagementModal;
